import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatGs, LOCATION_NAMES, formatDate } from "@/lib/format";
import { PageHeader, Btn, Modal, Input } from "@/components/ui/kit";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, Wallet } from "lucide-react";
import { PurchaseInvoiceModal } from "@/components/purchases/PurchaseInvoiceModal";
import { PurchaseDetailModal } from "@/components/purchases/PurchaseDetailModal";
import { PurchaseFilters, filterPurchases, EMPTY_FILTERS } from "@/components/purchases/PurchaseFilters";

const EXP_TYPES = [["fijo", "Fijo"], ["variable", "Variable"], ["ocasional", "Ocasional"], ["salario", "Salario"]];

export default function Purchases() {
  const { location } = useAuth();
  const [tab, setTab] = useState("compras");
  const [purchases, setPurchases] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [products, setProducts] = useState([]);
  const [showPurchase, setShowPurchase] = useState(false);
  const [showExpense, setShowExpense] = useState(false);

  const [initial, setInitial] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // expense form
  const [exp, setExp] = useState({ type: "fijo", category: "", description: "", amount: 0, date: "" });

  const load = async () => {
    try {
      const [p, e, pr] = await Promise.all([
        api.get("/purchases", { params: { location } }),
        api.get("/expenses", { params: { location } }),
        api.get("/products", { params: { location } }),
      ]);
      setPurchases(p.data); setExpenses(e.data); setProducts(pr.data);
    } catch (err) { toast.error(apiErr(err)); }
  };
  useEffect(() => { load(); }, [location]);
  const routerLoc = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const st = routerLoc.state;
    if (!st?.reorder?.length) return;
    if (st.location === location) {
      setTab("compras");
      setInitial({ items: st.reorder, supplier: st.supplier || "" });
      setShowPurchase(true);
      toast.info("Compra de reposición armada: revisá cantidades y costos y registrá");
    }
    navigate(routerLoc.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.state]);

  const suppliers = useMemo(() => [...new Set([...purchases.map((p) => p.supplier), ...products.map((p) => p.supplier)].map((s) => (s || "").trim()).filter(Boolean))].sort(), [purchases, products]);
  const shown = useMemo(() => filterPurchases(purchases, filters), [purchases, filters]);
  const shownTotal = shown.reduce((s, p) => s + p.total, 0);
  const openNew = () => { setInitial(null); setShowPurchase(true); };

  const saveExpense = async () => {
    if (!exp.category || !exp.amount) { toast.error("Completa categoría y monto"); return; }
    try {
      await api.post("/expenses", { location, ...exp, amount: Number(exp.amount), date: exp.date || null });
      toast.success("Gasto registrado");
      setShowExpense(false); setExp({ type: "fijo", category: "", description: "", amount: 0, date: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const delExpense = async (id) => { try { await api.delete(`/expenses/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <PageHeader title="Compras & Gastos" subtitle={LOCATION_NAMES[location]}>
        {tab === "compras"
          ? <Btn onClick={openNew} data-testid="new-purchase-btn"><Plus className="w-4 h-4" /> Nueva Compra</Btn>
          : <Btn onClick={() => setShowExpense(true)} data-testid="new-expense-btn"><Plus className="w-4 h-4" /> Nuevo Gasto</Btn>}
      </PageHeader>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("compras")} data-testid="tab-compras" className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${tab === "compras" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}><Receipt className="w-4 h-4" /> Compras de mercadería</button>
        <button onClick={() => setTab("gastos")} data-testid="tab-gastos" className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${tab === "gastos" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}><Wallet className="w-4 h-4" /> Gastos del local</button>
      </div>

      {tab === "compras" ? (
        <div>
        <PurchaseFilters f={filters} setF={setFilters} suppliers={suppliers} />
        <div className="flex justify-between text-sm text-muted-foreground mb-2" data-testid="purchase-filter-summary">
          <span>{shown.length} de {purchases.length} facturas</span>
          <span>Total filtrado: <span className="font-data font-bold text-primary">{formatGs(shownTotal)}</span></span>
        </div>
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="purchases-table">
            <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase">
              <tr><th className="text-left p-3">Fecha</th><th className="text-left p-3">N° Factura</th><th className="text-left p-3">Proveedor</th><th className="text-left p-3">Comprobante</th><th className="text-left p-3">Ítems</th><th className="text-right p-3">Total</th></tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} onClick={() => setDetail(p)} data-testid={`purchase-item-${p.id}`} className="border-t border-border hover:bg-accent/30 cursor-pointer">
                  <td className="p-3 text-muted-foreground">{p.invoice_date ? p.invoice_date.split("-").reverse().join("/") : formatDate(p.timestamp)}</td>
                  <td className="p-3 font-data">{p.invoice_number || "—"}</td>
                  <td className="p-3">{p.supplier || "—"}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${p.invoice_type === "con_factura" ? "border-emerald-500/30 text-emerald-500" : "border-border text-muted-foreground"}`}>{p.invoice_type === "con_factura" ? "Con Factura" : "Sin Factura"}</span></td>
                  <td className="p-3 text-muted-foreground max-w-xs truncate">{p.items.map((i) => `${i.name} x${i.quantity}`).join(", ")}</td>
                  <td className="p-3 text-right font-data font-bold">{formatGs(p.total)}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{purchases.length ? "Ninguna factura coincide con el filtro" : "Sin compras registradas"}</td></tr>}
            </tbody>
          </table>
        </div>
        </div>
      ) : (
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase">
              <tr><th className="text-left p-3">Fecha</th><th className="text-left p-3">Tipo</th><th className="text-left p-3">Categoría</th><th className="text-left p-3">Descripción</th><th className="text-right p-3">Monto</th><th></th></tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id} className="border-t border-border hover:bg-accent/30">
                  <td className="p-3 text-muted-foreground">{formatDate(e.date)}</td>
                  <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full border border-primary/30 text-primary capitalize">{e.type}</span></td>
                  <td className="p-3 font-medium">{e.category}</td>
                  <td className="p-3 text-muted-foreground">{e.description}</td>
                  <td className="p-3 text-right font-data font-bold">{formatGs(e.amount)}</td>
                  <td className="p-3 text-right"><button onClick={() => delExpense(e.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Sin gastos registrados</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Purchase modal */}
      <PurchaseInvoiceModal open={showPurchase} onClose={() => setShowPurchase(false)} onSaved={() => { setShowPurchase(false); setInitial(null); load(); }}
        products={products} location={location} suppliers={suppliers} initial={initial} />
      <PurchaseDetailModal purchase={detail} onClose={() => setDetail(null)} />

      {/* Expense modal */}
      <Modal open={showExpense} onClose={() => setShowExpense(false)} title="Registrar gasto">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Tipo de gasto</label>
            <div className="grid grid-cols-4 gap-2">
              {EXP_TYPES.map(([v, l]) => (
                <button key={v} onClick={() => setExp({ ...exp, type: v })} data-testid={`exp-type-${v}`} className={`h-10 rounded-lg text-xs font-medium border transition-colors ${exp.type === v ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Categoría (ej: Alquiler, Luz, Combustible, Sueldo Juan)</label><Input value={exp.category} onChange={(e) => setExp({ ...exp, category: e.target.value })} data-testid="exp-category" /></div>
          <div><label className="text-sm font-medium mb-1 block">Descripción</label><Input value={exp.description} onChange={(e) => setExp({ ...exp, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Monto (Gs.)</label><Input type="number" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} data-testid="exp-amount" /></div>
            <div><label className="text-sm font-medium mb-1 block">Fecha</label><Input type="date" value={exp.date} onChange={(e) => setExp({ ...exp, date: e.target.value })} /></div>
          </div>
          <div className="flex gap-2">
            <Btn variant="outline" onClick={() => setShowExpense(false)} className="flex-1">Cancelar</Btn>
            <Btn onClick={saveExpense} data-testid="save-expense" className="flex-1">Guardar gasto</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
